import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { api } from '@/lib/api'
import type { Learning, LearningFields } from '@/types/learning'

export const learningKeys = {
  all: ['learnings'] as const,
  list: (filters: Record<string, string>) => ['learnings', 'list', filters] as const,
  detail: (id: string) => ['learnings', 'detail', id] as const
}

export function useLearnings(filters: Record<string, string>) {
  const query = Object.fromEntries(Object.entries(filters).filter(([, v]) => v))
  return useQuery({
    queryKey: learningKeys.list(query),
    queryFn: () => api<{ data: Learning[] }>('/support-tickets/learnings', { query })
  })
}

export function useLearning(id: string | undefined) {
  return useQuery({
    queryKey: learningKeys.detail(id ?? ''),
    queryFn: () => api<Learning>(`/support-tickets/learnings/${id}`),
    enabled: Boolean(id)
  })
}

export type LearningInput = LearningFields & { sourceInvestigationId?: string; expectedVersion?: number }

export function useSaveLearning(id?: string) {
  const qc = useQueryClient()
  return useMutation<Learning, Error, LearningInput>({
    mutationFn: body => api<Learning>(id ? `/support-tickets/learnings/${id}` : '/support-tickets/learnings', { method: id ? 'PUT' : 'POST', body }),
    onSuccess: () => qc.invalidateQueries({ queryKey: learningKeys.all })
  })
}

export function useSetLearningActive(id: string) {
  const qc = useQueryClient()
  return useMutation<Learning, Error, boolean>({
    mutationFn: active => api<Learning>(`/support-tickets/learnings/${id}/active`, { method: 'POST', body: { active } }),
    onSuccess: () => qc.invalidateQueries({ queryKey: learningKeys.all })
  })
}
